import { skipToken } from '@reduxjs/toolkit/query';
import { SystemAdminFormTypes } from 'helper/types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetSystemFleetsAttributesQuery } from 'services/systemAdmin/fleets';
import { normalizeRecordData } from 'services/utils';
import { useListVehiclesV2Query } from 'services/vehiclesV2';
import { getSelectedFleet } from 'store/slices/settingsPageSlice';
import {
    addSelectedVehicles,
    clearSelectedVehicles,
    getSelectedVehiclesFromFleetForm,
    getSystemFleetsFormType,
    removeSelectedVehicle,
} from 'store/slices/systemFleet';

import TableSkeleton from '../TableSkeleton';

import './_styles.scss';

import SelectVehiclesTableContent from './SelectVehiclesTableContent';

export const tableHeaders = {
    vehicleName: 'Vehicle name',
    vin: 'VIN',
    licensePlate: 'License plate',
    owner: 'Owner',
};

const SelectVehiclesTable = () => {
    const dispatch = useDispatch();
    const selectedFleet = useSelector(getSelectedFleet);
    const selectedVehiclesFromEditedFleet = useSelector(getSelectedVehiclesFromFleetForm);
    const systemFleetsFormType = useSelector(getSystemFleetsFormType);

    const { isLoading: isLoadingSystemVehicles, availableVehicles } = useListVehiclesV2Query(
        {},
        {
            refetchOnMountOrArgChange: true,
            selectFromResult: state => ({
                ...state,
                availableVehicles: normalizeRecordData(state.data).filter(
                    _ => _.fleetId === null || _.fleetId === selectedFleet?.id
                ),
            }),
        }
    );

    const { isLoading: isLoadingFleetVehicles, fleetVehicles } = useGetSystemFleetsAttributesQuery(
        selectedFleet?.id ?? skipToken,
        {
            refetchOnMountOrArgChange: true,
            selectFromResult: state => {
                const vins = state.data?.vehicles?.map(_ => _?.vin);
                return {
                    ...state,
                    fleetVehicles: vins?.length ? vins : [],
                };
            },
        }
    );

    useEffect(() => {
        if (
            !selectedVehiclesFromEditedFleet &&
            fleetVehicles.length &&
            systemFleetsFormType === SystemAdminFormTypes.EDIT_FORM
        ) {
            dispatch(addSelectedVehicles(fleetVehicles as string[]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fleetVehicles, selectedFleet]);

    const onVehicleSelect = (vin: string) => {
        if (selectedVehiclesFromEditedFleet?.includes(vin)) {
            dispatch(removeSelectedVehicle(vin));
        } else {
            dispatch(addSelectedVehicles([vin]));
        }
    };

    const selectAll = () => {
        if (availableVehicles) {
            const vins = availableVehicles.map(_ => _.vin);
            dispatch(addSelectedVehicles(vins));
        }
    };

    const deselectAll = () => {
        dispatch(clearSelectedVehicles());
    };

    if (isLoadingSystemVehicles || isLoadingFleetVehicles) {
        return <TableSkeleton id='edit-fleets-select-vehicles' tableHeaders={tableHeaders} />;
    }

    return (
        <SelectVehiclesTableContent
            selectedVehiclesFromEditedFleet={selectedVehiclesFromEditedFleet}
            availableVehicles={availableVehicles}
            fleetVehicles={fleetVehicles}
            isLoading={isLoadingSystemVehicles || isLoadingFleetVehicles}
            selectAll={selectAll}
            deselectAll={deselectAll}
            onVehicleSelect={onVehicleSelect}
        />
    );
};

export default SelectVehiclesTable;
